import React, { useEffect, useState } from "react";
import Instance from "../Services/Instance";
import config from "../Services/config.js";
import { Eye, MessageSquare, CheckCircle, XCircle, Clock, AlertCircle, Filter, Send } from "lucide-react";
import SideBar from "../Layouts/SideBar";
import "../Assets/Styles/Tickets.css";

function TicketManagementDashboard() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [reponse, setReponse] = useState("");
  const [statutFilter, setStatutFilter] = useState("all");
  const [problemeFilter, setProblemeFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [adminUser, setAdminUser] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setAdminUser(parsedUser);
      } catch (error) {
        console.error("Erreur lors de la récupération du user depuis le localStorage:", error);
        setError("Erreur d'authentification. Veuillez vous reconnecter.");
      }
    } else {
      setError("Aucun utilisateur trouvé. Veuillez vous reconnecter.");
    }
  }, []);

  const getUserFullName = (userId, ticket) => {
    if (ticket?.isGuest&& ticket?.guestfirstName && ticket?.guestlastName) {
       return `${ticket.guestfirstName} ${ticket.guestlastName}`;
    }
    if (!userId) return "N/A";
    if (typeof userId === 'object' && userId.firstName && userId.lastName) {
      return `${userId.firstName} ${userId.lastName}`;
    }
    return userId.toString();
  };

  const getUserEmail = (userId, ticket) => {
    if (ticket?.isGuest && ticket?.guestEmail) {
      return ticket.guestEmail;
    }
    if (userId?.email) {
      return userId.email;
    }
    return "N/A";
  };

  const formatDate = (date) => {
    if (!date) return "-";
    const dateObject = new Date(date);
    return dateObject.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  useEffect(() => {
    fetchTickets();
  }, [statutFilter, problemeFilter]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      let url = `${config.api_url}/tickets/admin/all?page=${currentPage}&limit=${itemsPerPage}`;
      
      if (statutFilter !== 'all') url += `&statut=${statutFilter}`;
      if (problemeFilter !== 'all') url += `&probleme=${problemeFilter}`;

      const result = await Instance.get(url);
      setTickets(result.data.tickets || []);
    } catch (error) {
      setError(error.response?.data?.message || error.message || "Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async () => {
    if (!reponse.trim()) {
      setError("Veuillez entrer une réponse");
      setTimeout(() => setError(""), 3000);
      return;
    }

    if (!adminUser || !adminUser._id) {
      setError("Erreur d'authentification. Veuillez vous reconnecter.");
      setTimeout(() => setError(""), 3000);
      return;
    }

    try {
      await Instance.put(`${config.api_url}/tickets/admin/respond/${selectedTicket._id}`, {
        reponse: reponse,
        statut: "Résolu",
        adminId: adminUser._id
      });

      setTickets(prev => prev.map(ticket => 
        ticket._id === selectedTicket._id 
          ? { ...ticket, reponse: reponse, statut: "Résolu", dateReponse: new Date() }
          : ticket
      ));

      setShowModal(false);
      setReponse("");
      setSelectedTicket(null);
      alert("Réponse envoyée avec succès!");
    } catch (error) {
      setError(error.response?.data?.message || error.message);
      setTimeout(() => setError(""), 3000);
    }
  };

  const updateStatus = async (ticketId, newStatut) => {
    try {
      await Instance.put(`${config.api_url}/tickets/admin/status/${ticketId}`, {
        statut: newStatut
      });

      setTickets(prev => prev.map(ticket => 
        ticket._id === ticketId 
          ? { ...ticket, statut: newStatut }
          : ticket
      ));
    } catch (error) {
      setError(error.response?.data?.message || error.message);
      setTimeout(() => setError(""), 3000);
    }
  };

  const getStatutClass = (statut) => {
    switch (statut) {
      case "En attente": return "badge-status-pending";
      case "En cours": return "badge-status-progress";
      case "Résolu": return "badge-status-resolved";
      case "Fermé": return "badge-status-closed";
      default: return "badge-status-closed";
    }
  };

  const getPrioriteClass = (priorite) => {
    switch (priorite) {
      case "Urgente": return "badge-priority-urgent";
      case "Haute": return "badge-priority-high";
      case "Moyenne": return "badge-priority-medium";
      case "Basse": return "badge-priority-low";
      default: return "badge-status-closed";
    }
  };

  const getStatutIcon = (statut) => {
    switch (statut) {
      case "En attente": return <Clock className="badge-icon" />;
      case "En cours": return <AlertCircle className="badge-icon" />;
      case "Résolu": return <CheckCircle className="badge-icon" />;
      case "Fermé": return <XCircle className="badge-icon" />;
      default: return <AlertCircle className="badge-icon" />;
    }
  };

  const showTicketDetails = (ticket) => {
    setSelectedTicket(ticket);
    setReponse(ticket.reponse || "");
    setShowModal(true);
  };

  const ticketsArray = Array.isArray(tickets) ? tickets : [];

  const filteredTickets = ticketsArray.filter(ticket => {
    if (statutFilter !== 'all' && ticket.statut !== statutFilter) return false;
    if (problemeFilter !== 'all' && ticket.probleme !== problemeFilter) return false;
    return true;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTickets.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredTickets.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <div className="loading-container">
        <SideBar />
        <div className="loading-spinner-wrapper">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="ticket-container">
      <SideBar />
      
      <div className="ticket-main">
        <div className="ticket-content">
          <div className="ticket-card">
            <div className="ticket-header">
              <div className="header-top">
                <div className="header-title">
                  <h1>🎫 Gestion des Tickets Support</h1>
                  <p>Gérez les demandes de support client</p>
                </div>
                
                <div className="header-filters">
                  <div className="filter-group">
                    <Filter className="filter-icon" />
                    <select
                      value={statutFilter}
                      onChange={(e) => setStatutFilter(e.target.value)}
                      className="filter-select"
                    >
                      <option value="all">Tous les statuts</option>
                      <option value="En attente">En attente</option>
                      <option value="En cours">En cours</option>
                      <option value="Résolu">Résolu</option>
                      <option value="Fermé">Fermé</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="stats-grid">
                <div className="stat-card total">
                  <div className="stat-label">Total</div>
                  <div className="stat-value">{ticketsArray.length}</div>
                </div>
                <div className="stat-card pending">
                  <div className="stat-label">En attente</div>
                  <div className="stat-value">
                    {ticketsArray.filter(t => t.statut === 'En attente').length}
                  </div>
                </div>
                <div className="stat-card progress">
                  <div className="stat-label">En cours</div>
                  <div className="stat-value">
                    {ticketsArray.filter(t => t.statut === 'En cours').length}
                  </div>
                </div>
                <div className="stat-card resolved">
                  <div className="stat-label">Résolus</div>
                  <div className="stat-value">
                    {ticketsArray.filter(t => t.statut === 'Résolu').length}
                  </div>
                </div>
                <div className="stat-card closed">
                  <div className="stat-label">Fermés</div>
                  <div className="stat-value">
                    {ticketsArray.filter(t => t.statut === 'Fermé').length}
                  </div>
                </div>
              </div>
            </div>

            <div className="table-container">
              <table className="ticket-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Client</th>
                    <th>Titre</th>
                    <th>Commande</th>
                    <th>Problème</th>
                    <th>Priorité</th>
                    <th>Statut</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((ticket) => (
                    <tr key={ticket._id}>
                      <td className="cell-id">
                        <div className="cell-truncate" title={ticket._id}>
                          #{ticket._id.slice(-6)}
                        </div>
                      </td>
                      <td>
                        <div className="client-name">
                          {getUserFullName(ticket.userId, ticket)}
                        </div>
                        <div className="client-email">
                          {getUserEmail(ticket.userId, ticket)}
                        </div>
                        {ticket.isGuest && (
                          <span className="guest-badge">👤 Invité</span>
                        )}
                      </td>
                      <td>
                        <div className="cell-truncate-large" title={ticket.titre}>
                          {ticket.titre}
                        </div>
                      </td>
                      <td>{ticket.numeroCommande}</td>
                      <td>
                        <span className="badge badge-purple">
                          {ticket.probleme}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${getPrioriteClass(ticket.priorite)}`}>
                          {ticket.priorite}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${getStatutClass(ticket.statut)}`}>
                          {getStatutIcon(ticket.statut)}
                          {ticket.statut}
                        </span>
                      </td>
                      <td>{formatDate(ticket.createdAt)}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            onClick={() => showTicketDetails(ticket)}
                            className="action-btn action-btn-blue"
                            title="Voir et répondre"
                          >
                            <MessageSquare className="action-icon" />
                          </button>
                          
                          {ticket.statut === "En attente" && (
                            <button
                              onClick={() => updateStatus(ticket._id, "En cours")}
                              className="action-btn action-btn-orange"
                              title="Mettre en cours"
                            >
                              <Clock className="action-icon" />
                            </button>
                          )}
                          
                          {(ticket.statut === "Résolu" || ticket.statut === "En cours") && (
                            <button
                              onClick={() => updateStatus(ticket._id, "Fermé")}
                              className="action-btn action-btn-gray"
                              title="Fermer le ticket"
                            >
                              <XCircle className="action-icon" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pagination-container">
              <div className="pagination-wrapper">
                <div className="pagination-mobile">
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="pagination-btn"
                  >
                    Précédent
                  </button>
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="pagination-btn"
                  >
                    Suivant
                  </button>
                </div>
                <div className="pagination-desktop">
                  <div>
                    <p className="pagination-info">
                      Affichage de <span>{indexOfFirstItem + 1}</span> à{' '}
                      <span>{Math.min(indexOfLastItem, filteredTickets.length)}</span> sur{' '}
                      <span>{filteredTickets.length}</span> tickets
                    </p>
                  </div>
                  <div>
                    <nav className="pagination-nav">
                      <button
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="pagination-arrow"
                      >
                        ‹
                      </button>
                      
                      {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                        const pageNumber = i + 1;
                        return (
                          <button
                            key={pageNumber}
                            onClick={() => paginate(pageNumber)}
                            className={`pagination-page-btn ${currentPage === pageNumber ? 'active' : ''}`}
                          >
                            {pageNumber}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="pagination-arrow"
                      >
                        ›
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {showModal && selectedTicket && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-body">
                <div className="modal-header">
                  <div className="modal-title">
                    <h3>Ticket #{selectedTicket._id.slice(-6)}</h3>
                    <p>{selectedTicket.titre}</p>
                  </div>
                  <button onClick={() => setShowModal(false)} className="modal-close">
                    <XCircle className="modal-close-icon" />
                  </button>
                </div>
                
                <div className="modal-sections">
                  <div className="info-section client">
                    <h4>
                      👤 Client {selectedTicket.isGuest && <span style={{color: '#2563eb', fontSize: '0.875rem'}}>(Invité)</span>}
                    </h4>
                    <p>
                      <span className="label">Nom:</span> {getUserFullName(selectedTicket.userId, selectedTicket)}
                    </p>
                    <p>
                      <span className="label">Email:</span> {getUserEmail(selectedTicket.userId, selectedTicket)}
                    </p>
                    <p>
                      <span className="label">Commande:</span> {selectedTicket.numeroCommande}
                    </p>
                  </div>

                  <div className="info-section details">
                    <h4>📋 Détails</h4>
                    
                    <p>
                      <span className="label">Statut:</span> 
                      <span className={`badge ${getStatutClass(selectedTicket.statut)}`} style={{marginLeft: '0.5rem'}}>
                        {selectedTicket.statut}
                      </span>
                    </p>
                    <div className="description">
                      <span className="label">Description:</span>
                      <p>{selectedTicket.description}</p>
                    </div>
                  </div>

                  {selectedTicket.reponse && (
                    <div className="info-section response">
                      <h4>✅ Réponse envoyée</h4>
                      <p>{selectedTicket.reponse}</p>
                      <p className="response-date">Le {formatDate(selectedTicket.dateReponse)}</p>
                    </div>
                  )}

                  <div className="form-group">
                    <label className="form-label">
                      {selectedTicket.reponse ? "Modifier la réponse" : "Répondre au client"}
                    </label>
                    <textarea
                      value={reponse}
                      onChange={(e) => setReponse(e.target.value)}
                      placeholder="Écrivez votre réponse ici..."
                      rows="6"
                      className="form-textarea"
                    />
                  </div>

                  <div className="modal-actions">
                    <button
                      onClick={handleRespond}
                      disabled={!reponse.trim() || !adminUser}
                      className="btn-submit"
                    >
                      <Send className="action-icon" />
                      Envoyer la réponse
                    </button>
                    <button
                      onClick={() => setShowModal(false)}
                      className="btn-cancel"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="error-toast">
            <p>Erreur</p>
            <p>{error}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default TicketManagementDashboard;